import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Typography, 
  Box, 
  Tabs, 
  Tab,
  Chip,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontWeight: 'bold',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
}));

function ExcelPreview({ base64Content, filename }) {
  const [workbook, setWorkbook] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (base64Content) {
      try {
        setLoading(true);
        setError(null);

        // Convert base64 to ArrayBuffer
        const binaryString = atob(base64Content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        // Read the Excel file
        const wb = XLSX.read(bytes, { type: 'array' });
        setWorkbook(wb);

        // Parse all sheets
        const sheetsData = wb.SheetNames.map(sheetName => {
          const worksheet = wb.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
            header: 1, 
            defval: '',
            raw: false 
          });
          
          return {
            name: sheetName,
            data: jsonData,
            range: worksheet['!ref'] || 'A1'
          };
        });

        setSheets(sheetsData);
        setActiveSheet(0);
      } catch (err) {
        console.error('Error parsing Excel file:', err);
        setError('Fehler beim Laden der Excel-Datei: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
  }, [base64Content]);

  const handleSheetChange = (event, newValue) => {
    setActiveSheet(newValue);
  };

  const renderTable = (sheetData) => {
    if (!sheetData || !sheetData.data || sheetData.data.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          Dieses Arbeitsblatt enthält keine Daten.
        </Alert>
      );
    }

    const data = sheetData.data;
    const headers = data[0] || [];
    const rows = data.slice(1);

    // Limit display to prevent performance issues
    const maxRows = 100;
    const displayRows = rows.slice(0, maxRows);
    const hasMoreRows = rows.length > maxRows;

    return (
      <Box>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" component="h3">
            {sheetData.name}
          </Typography>
          <Chip 
            label={`${data.length - 1} Zeilen x ${headers.length} Spalten`} 
            color="primary" 
            size="small" 
          />
          {hasMoreRows && (
            <Chip 
              label={`Zeige erste ${maxRows} Zeilen`} 
              color="warning" 
              size="small" 
            />
          )}
        </Box>
        
        <TableContainer component={Paper}>
          <Table stickyHeader size="small">
            {headers.length > 0 && (
              <TableHead>
                <TableRow>
                  <StyledTableCell sx={{ minWidth: 50 }}>#</StyledTableCell>
                  {headers.map((header, index) => (
                    <StyledTableCell key={index} sx={{ minWidth: 120 }}>
                      {header || `Spalte ${index + 1}`}
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
            )}
            <TableBody>
              {displayRows.map((row, rowIndex) => (
                <StyledTableRow key={rowIndex}>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>
                    {rowIndex + 2}
                  </TableCell>
                  {headers.map((_, colIndex) => (
                    <TableCell
                      key={colIndex}
                      sx={{
                        whiteSpace: 'pre-wrap', // Beibehaltung der Zeilenumbrüche ohne zusätzliche Leerzeilen
                        wordWrap: 'break-word' // Sorgt dafür, dass der Text nicht außerhalb der Zelle überläuft
                      }}
                    >
                      {row[colIndex]?.toString().trim() || ''}
                    </TableCell>
                  ))}
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {hasMoreRows && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Es werden nur die ersten {maxRows} Zeilen angezeigt. 
            Laden Sie die Datei herunter, um alle Daten zu sehen.
          </Alert>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Lade Excel-Datei...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!sheets || sheets.length === 0) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Keine Arbeitsblätter in der Excel-Datei gefunden.
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
        {filename}
      </Typography>
      
      {sheets.length > 1 && (
        <Tabs value={activeSheet} onChange={handleSheetChange} sx={{ mb: 2 }}>
          {sheets.map((sheet, index) => (
            <Tab key={index} label={sheet.name} />
          ))}
        </Tabs>
      )}
      
      {sheets[activeSheet] && renderTable(sheets[activeSheet])}
    </Box>
  );
}

export default ExcelPreview;